import {
  useState,
  useRef,
  useMemo,
  RefObject,
  Dispatch,
  SetStateAction,
  useEffect,
  MutableRefObject,
} from 'react'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import {
  ActionBeginEventArgs,
  RichTextEditorComponent,
} from '@syncfusion/ej2-react-richtexteditor'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'

export interface IEditorInput {
  text: string
  htmlContent: string
}

export interface ISPTEHooks {
  handleSend: () => void
  handleToolbarActions: (buttonName: string) => void
  mentionRef: RefObject<MentionComponent>
  onActionBegin: (e: ActionBeginEventArgs) => void
  onCreated: () => void
  onTextChange: () => void
  peopleList: { name: string }[]
  saveCaretPosition: () => void
  setVariablePopupOpen: (open: boolean) => void
  showVariables: boolean
  toolbarRef: RefObject<RichTextEditorComponent> | null
  variableRef: RefObject<MentionComponent>
}

const useSPTextEditor = (
  editorInput: IEditorInput,
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>,
  onSend: (plainText: string, htmlContent: string) => void,
  toolbarRef: RefObject<RichTextEditorComponent> | null,
  isEditing?: boolean,
  selectionRef?: MutableRefObject<Range | null>,
): ISPTEHooks => {
  const patients = useAppSelector((state) => state.patient.data)
  const [isVariablePopupOpen, setIsVariablePopupOpen] = useState<boolean>(false)
  const [showVariables, setShowVariables] = useState<boolean>(false)
  const mentionRef = useRef<MentionComponent>(null)
  const variableRef = useRef<MentionComponent>(null)

  const peopleList = useMemo(() => {
    return patients.map((patient) => ({ name: getFullName(patient) }))
  }, [patients])

  const setVariablePopupOpen = (open: boolean): void =>
    setIsVariablePopupOpen(open)

  const activeToolbarKey = isVariablePopupOpen ? 'variable' : null

  useEffect(() => {
    if (!toolbarRef?.current) return
    const toolbar = toolbarRef.current.getToolbar()
    if (!toolbar) return
    const customKeys = ['ai', 'variable']
    customKeys.forEach((key) => {
      const btn = toolbar.querySelector(`.custom-toolbar-button.${key}`)
      const toolbarItem = btn?.closest('.e-toolbar-item')
      if (toolbarItem) {
        if (key === activeToolbarKey) {
          toolbarItem.classList.add('e-active')
        } else {
          toolbarItem.classList.remove('e-active')
        }
      }
    })
  }, [activeToolbarKey, toolbarRef])

  const handleToolbarActions = (buttonName: string): void => {
    if (toolbarRef) {
      const ref = toolbarRef.current

      switch (buttonName) {
        case 'variable':
          {
            if (variableRef.current && ref) {
              if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
              variableRef.current.showPopup()
            }
          }
          break
        case 'mention':
          if (mentionRef.current && ref) {
            if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
            mentionRef.current.showPopup()
          }
          break
        default:
          break
      }
    }
  }

  const handleSend = (): void => {
    const _plainText = toolbarRef?.current?.getText() ?? ''
    const _htmlContent = toolbarRef?.current?.getHtml() ?? ''

    const plainText = _plainText.trimEnd()
    if (!plainText) return

    let htmlContent = _htmlContent || _plainText
    if (htmlContent) {
      // Remove trailing <br> from the end of the content
      htmlContent = htmlContent.replace(
        /(?:<br\s*\/?>\s*)+(\s*<\/p>)?$/gi,
        '$1',
      )
    }

    onSend(plainText, htmlContent || plainText)

    setEditorInput({
      text: '',
      htmlContent: '',
    })
    if (toolbarRef?.current) {
      toolbarRef.current.value = ''
    }
  }

  const onActionBegin = (e: ActionBeginEventArgs): void => {
    if (e.requestType === 'EnterAction') {
      const originalEvent = (e as any).originalEvent as
        | KeyboardEvent
        | undefined

      const isShiftEnter = originalEvent?.shiftKey
      if (!isShiftEnter) {
        // Treat plain Enter as "send" and prevent RTE from inserting a <br>
        e.cancel = true
        handleSend()
      }
    }
  }

  const saveCaretPosition = (): void => {
    const rte = toolbarRef?.current
    if (rte && rte.formatter?.editorManager?.nodeSelection) {
      const range = rte.formatter.editorManager.nodeSelection.getRange(document)
      if (range && selectionRef) {
        selectionRef.current = range
      }
    }
  }

  const onTextChange = (): void => {
    if (toolbarRef?.current) {
      const _plainText = toolbarRef.current.getText()
      setEditorInput({
        text: _plainText,
        htmlContent: toolbarRef.current.getHtml(),
      })
    }
    saveCaretPosition()
  }

  const onCreated = (): void => {
    if (toolbarRef?.current) {
      const editorElement =
        toolbarRef.current.element?.querySelector('.e-content')
      if (editorElement) {
        editorElement.addEventListener('input', onTextChange)
        editorElement.addEventListener('paste', () =>
          setTimeout(onTextChange, 0),
        )
        editorElement.addEventListener('drop', () =>
          setTimeout(onTextChange, 0),
        )
      }
    }
  }

  useEffect(() => {
    return () => {
      if (toolbarRef?.current) {
        const editorElement =
          toolbarRef.current.element?.querySelector('.e-content')
        if (editorElement) {
          editorElement.removeEventListener('input', onTextChange)
          editorElement.removeEventListener('paste', () =>
            setTimeout(onTextChange, 0),
          )
          editorElement.removeEventListener('drop', () =>
            setTimeout(onTextChange, 0),
          )
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isEditing) {
      if (toolbarRef?.current) toolbarRef.current.value = editorInput.text
    }
  }, [isEditing, editorInput])

  return {
    handleSend,
    handleToolbarActions,
    mentionRef,
    onActionBegin,
    onCreated,
    onTextChange,
    peopleList,
    saveCaretPosition,
    setVariablePopupOpen,
    showVariables,
    toolbarRef,
    variableRef,
  }
}

export default useSPTextEditor
